import { assertUnknownPathsUntouched } from './guard'
import { assignJsonKey, isPlainObject, jsonEqual, valueAt } from './json'
import { mergePatch } from './merge-patch'
import { getRaw, hasRaw } from './raw'
import { getUnknown } from './unknown'

/** Wire fragment for fields changed on the view. Paths in `$unknown` are left out when the edit would overwrite them. */
export function editsFromView(
  entity: object,
  keys: readonly string[]
): Record<string, unknown> {
  const raw = getRaw(entity)
  const rawRecord = isPlainObject(raw) ? raw : {}
  const unknown = getUnknown(entity)
  const view = entity as Record<string, unknown>
  const edits: Record<string, unknown> = {}

  for (const key of keys) {
    const current = view[key]
    if (current === undefined || current === null) {
      if (pathBlocked(unknown, key)) {
        continue
      }
      if (Object.prototype.hasOwnProperty.call(rawRecord, key)) {
        assignJsonKey(edits, key, null)
      }
      continue
    }

    const wired = wireValue(current)
    if (wouldChangeUnknown(unknown, key, wired, rawRecord[key])) {
      continue
    }
    if (jsonEqual(wired, rawRecord[key])) {
      continue
    }

    assignJsonKey(edits, key, wired)
  }

  return edits
}

function pathBlocked(unknown: readonly string[], key: string): boolean {
  return unknown.some(path => path === key || path.startsWith(`${key}.`))
}

function wouldChangeUnknown(
  unknown: readonly string[],
  key: string,
  wired: unknown,
  rawValue: unknown
): boolean {
  for (const path of unknown) {
    if (path !== key && !path.startsWith(`${key}.`)) {
      continue
    }

    const relative = path === key ? '' : path.slice(key.length + 1)
    const wiredAt = relative ? valueAt(wired, relative) : wired
    const rawAt = relative ? valueAt(rawValue, relative) : rawValue
    if (!jsonEqual(wiredAt, rawAt)) {
      return true
    }
  }

  return false
}

/** Wire JSON for a nested object. `$raw` keeps unknown keys; view edits overlay it. */
export function wireEntity(entity: object): unknown {
  const wireUnprovisioned = (entity as { wireUnprovisioned?: () => unknown }).wireUnprovisioned
  if (typeof wireUnprovisioned === 'function') {
    const body = wireUnprovisioned.call(entity)
    if (body !== undefined) {
      return body
    }
  }

  if (hasRaw(entity)) {
    const raw = getRaw(entity)
    const body = mergePatch(raw, editsFromView(entity, Object.keys(entity)))
    assertUnknownPathsUntouched(raw, body, getUnknown(entity))
    return body
  }

  const toJSON = (entity as { toJSON?: () => unknown }).toJSON
  if (typeof toJSON === 'function') {
    return wireValue(toJSON.call(entity))
  }

  return wireValue(entity)
}

/** Plain JSON for a constructor argument that may already be a migrated instance. */
export function sourceJson(value: unknown): unknown {
  if (value && typeof value === 'object' && hasRaw(value)) {
    return wireEntity(value)
  }

  return value ?? {}
}

export function wireValue(value: unknown): unknown {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
  }

  if (Array.isArray(value)) {
    return value.map(item => wireValue(item))
  }

  if (value && typeof value === 'object') {
    if (!isPlainObject(value)) {
      if (hasRaw(value) || typeof (value as { toJSON?: unknown }).toJSON === 'function') {
        return wireEntity(value)
      }

      const out: Record<string, unknown> = {}
      for (const key of Object.keys(value)) {
        assignJsonKey(out, key, wireValue((value as Record<string, unknown>)[key]))
      }
      return out
    }

    const out: Record<string, unknown> = {}
    for (const key of Object.keys(value)) {
      assignJsonKey(out, key, wireValue(value[key]))
    }
    return out
  }

  return value
}
